// @ts-nocheck
import React from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useHeaderSearch } from "@/hooks/useHeaderSearch";
import { SortableHeader, type SortOrder } from "@/components/shared/SortableHeader";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Download,
  MoreHorizontal,
  Box,
  Printer,
  Edit,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Trash2,
  CheckCircle2,
  XCircle,
  TrendingUp,
  AlertOctagon,
  Layers,
  Truck,
  Clock,
  AlertCircle,
  ShieldCheck,
  FileText,
  Calendar,
  ChevronDown,
} from "lucide-react";
import {
  DateGroupedTable,
  DateGroupToggleButton,
  extractDateKey,
} from "@/components/shared/DateGroupedTable";
import { PendingFqcQueueTab } from "./components/PendingFqcQueueTab";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ProductionThumbnailCell } from "@/components/shared/ProductionThumbnailCell";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Card } from "@/components/ui/card";
import { cn, formatNumber, viIncludes } from "@/lib/utils";
import { fqcStageOf } from "@/lib/fqc-stage";
import { FqcStageBadge } from "@/components/quality/FqcStageBadge";
import { toast } from "sonner";
import { trpcClient } from "@/lib/trpc";
import { QCFinalForm } from "./QCFinalForm";

import FinishedGoodsQCReportPrintModal from "@/components/quality/print/FinishedGoodsQCReportPrintModal";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { PageFilterTags } from "@/components/shared/PageFilterTags";
import { ListPageShell } from "@/components/shared/ListPageShell";
import {
  CompactKpiRibbon,
  type CompactKpiItem,
} from "@/components/shared/CompactKpiRibbon";
import { type StatusSidebarItem } from "@/components/shared/StatusSidebar";
import { QCStatus } from "@/config/status-registry";
import {
  BulkActionBar,
  type BulkAction,
} from "@/components/shared/BulkActionBar";

// ── Build sidebar items from registry ──
import { useRowSelection } from "@/hooks/useRowSelection";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { executeBulkAction } from "@/utils/bulkActionUtils";
import { useConfirm } from "@/providers/ConfirmProvider";
import { ExportImportButtons } from "@/components/shared/ExportImportButtons";
import {
  InlineFilterRow,
  CHECKBOX_COL_NULL,
  type InlineFilterDefinition,
} from "@/components/shared/InlineFilterRow";
import { useExcelExport } from "@/hooks/useExcelExport";
import { downloadExcelTemplate, type ExcelColumnDef } from "@/lib/excel.utils";
import { emsTabClass } from "@/components/ui/ems-tabs";

const EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "finalQcId", label: "Mã Phiếu KCS", width: 16 },
  { key: "customerName", label: "Khách Hàng", width: 25 },
  { key: "productName", label: "Sản Phẩm", width: 25 },
  { key: "productionOrderId", label: "Mã LSX", width: 16 },
  { key: "checkDate", label: "Ngày Kiểm", width: 14 },
  { key: "qtyLot", label: "SL Lô", width: 14, format: "number" },
  { key: "qtyChecked", label: "SL Kiểm", width: 14, format: "number" },
  { key: "qtyPassed", label: "SL Đạt", width: 14, format: "number" },
  { key: "qtyFailed", label: "SL Lỗi", width: 14, format: "number" },
  { key: "status", label: "Trạng Thái KCS", width: 16 },
  { key: "inspector", label: "Người Kiểm", width: 18 },
];

const FILTER_FIELDS: InlineFilterDefinition[] = [
  { key: "checkbox", type: "none" },
  { key: "finalQcId", placeholder: "Mã phiếu...", type: "text" },
  { key: "otif", type: "none" },
  { key: "customerProduct", placeholder: "Khách hàng / SP / LSX...", type: "text" },
  { key: "product", type: "none" },
  { key: "checkDate", type: "none" },
  { key: "qty", type: "none" },
  {
    key: "status",
    placeholder: "Tất cả kết quả",
    type: "select",
    options: [
      { value: "PASSED", label: "Đạt (Passed)" },
      { value: "FAILED", label: "Không đạt (Failed)" },
      { value: "WARNING", label: "Cảnh báo (Warning)" },
      { value: "PENDING", label: "Chờ kiểm (Pending)" },
    ],
  },
  {
    key: "dispatchStatus",
    placeholder: "Tình trạng",
    type: "select",
    options: [
      { value: "PENDING_RECEIPT", label: "Chờ đóng gói" },
      { value: "WITHOUT_DELIVERY", label: "Chưa giao" },
      { value: "READY_FOR_DISPATCH", label: "Chờ gom xe" },
      { value: "WITH_DELIVERY", label: "Đã lên phiếu giao" },
    ],
  },
  { key: "actions", type: "none" },
];

const STATUS_ITEMS: StatusSidebarItem[] = Object.entries(QCStatus).map(
  ([key, cfg]) => ({
    key,
    label: cfg.label,
    color: cfg.color,
    dotColor: cfg.dotColor,
  }),
);

export default function QCFinalPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { hasPermission } = useRoleAccess();
  const routerSearch = (useRouterState({ select: (s) => s.location.search }) as Record<string, any>) || {};
  const [activeMainTab, setActiveMainTab] = useState<"pending_fqc" | "qc_reports">(
    routerSearch?.tab === "pending_fqc" ? "pending_fqc" : "qc_reports"
  );

  const handleMainTabChange = (tab: "pending_fqc" | "qc_reports") => {
    setActiveMainTab(tab);
    navigate({
      search: (prev: any) => ({
        ...prev,
        tab: tab === "pending_fqc" ? "pending_fqc" : undefined,
      }),
      replace: true,
    });
  };

  const { data: tabCounts } = useQuery({
    queryKey: ["qc-final-tab-counts"],
    // Quay lại danh sách sau khi xoá/sửa ở trang chi tiết luôn tải lại, không hiện bản cũ (FB-7KL5H3)
    refetchOnMount: "always",
    queryFn: () => trpcClient.qcFinal.getTabCounts.query(),
  });

  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [dispatchTab, setDispatchTab] = useState<"ALL" | "WITHOUT_DELIVERY" | "READY_FOR_DISPATCH" | "WITH_DELIVERY">("ALL");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [isDateGrouped, setIsDateGrouped] = useState(true);
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [editingCheck, setEditingCheck] = useState<any>(null);
  const [showReportModal, setShowReportModal] = useState(false);

  const [showFilter, setShowFilter] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  const { headerSearch } = useHeaderSearch();
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>(null);

  const handleSort = (field: string, order: SortOrder) => {
    setSortField(order ? field : null);
    setSortOrder(order);
    setCurrentPage(1);
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearAll = () => {
    setFilterValues({});
    setStatusFilter(null);
    setDispatchTab("ALL");
  };

  // Fetch ALL data for sidebar counts
  const { data, isLoading, error } = useQuery({
    queryKey: ["qc-final-all"],
    refetchOnMount: "always",
    queryFn: async () => {
      const paginatedData = await (trpcClient as any).qcFinal.list.query({
        page: 1,
        limit: 100,
      });
      return paginatedData.items || [];
    },
  });

  // Fetch stats for 4 KPI cards
  const { data: statsData } = useQuery({
    queryKey: ["qc-final-stats"],
    queryFn: async () => (trpcClient as any).qcFinal.getStats.query(),
  });

  const allItems: any[] = data || [];

  // Client-side filter + paginate
  const filteredData = useMemo(() => {
    let result = allItems;
    if (statusFilter) {
      result = result.filter((item) => item.status === statusFilter);
    }
    if (dispatchTab === "WITHOUT_DELIVERY") {
      result = result.filter(
        (item) =>
          (item.status === "PASSED" || item.status === "WARNING") &&
          !item.deliveryInfo?.hasDelivery,
      );
    } else if (dispatchTab === "READY_FOR_DISPATCH") {
      result = result.filter(
        (item) =>
          item.deliveryInfo?.isReadyForDispatch &&
          !item.deliveryInfo?.hasDelivery,
      );
    } else if (dispatchTab === "WITH_DELIVERY") {
      result = result.filter((item) => item.deliveryInfo?.hasDelivery);
    }

    if (filterValues.finalQcId) {
      result = result.filter((item) =>
        item.finalQcId?.toLowerCase().includes(filterValues.finalQcId.toLowerCase()),
      );
    }
    if (filterValues.customerProduct) {
      const term = filterValues.customerProduct.toLowerCase();
      result = result.filter(
        (item) =>
          item.customerName?.toLowerCase().includes(term) ||
          item.productName?.toLowerCase().includes(term) ||
          item.productionOrder?.orderId?.toLowerCase().includes(term) ||
          item.productionOrderId?.toLowerCase().includes(term),
      );
    }
    if (filterValues.status) {
      result = result.filter(
        (item) => item.status?.toUpperCase() === filterValues.status.toUpperCase(),
      );
    }
    if (filterValues.dispatchStatus === "PENDING_RECEIPT") {
      result = result.filter((item) => fqcStageOf(item) === "PENDING_RECEIPT");
    } else if (filterValues.dispatchStatus === "WITHOUT_DELIVERY") {
      result = result.filter(
        (item) =>
          (item.status === "PASSED" || item.status === "WARNING") &&
          !item.deliveryInfo?.hasDelivery,
      );
    } else if (filterValues.dispatchStatus === "READY_FOR_DISPATCH") {
      result = result.filter(
        (item) =>
          item.deliveryInfo?.isReadyForDispatch &&
          !item.deliveryInfo?.hasDelivery,
      );
    } else if (filterValues.dispatchStatus === "WITH_DELIVERY") {
      result = result.filter((item) => Boolean(item.deliveryInfo?.hasDelivery));
    }

    // Ô tìm kiếm trên header (?q): mã phiếu, khách hàng, sản phẩm, lệnh sản xuất
    if (headerSearch.trim()) {
      result = result.filter((item) =>
        [
          item.finalQcId,
          item.customerName,
          item.productionOrder?.customer?.shortName,
          item.productionOrder?.customer?.name,
          item.productName,
          item.productCode,
          item.productionOrder?.orderId,
          item.productionOrderId,
          item.inspectorName,
        ].some((v) => viIncludes(v == null ? "" : String(v), headerSearch.trim())),
      );
    }

    // Sắp xếp theo cột
    if (sortField && sortOrder) {
      const accessors: Record<string, (i: any) => any> = {
        finalQcId: (i) => i.finalQcId,
        deadline: (i) => {
          const d = i.otifInfo?.deadline || i.productionOrder?.deliveryDate;
          return d ? new Date(d).getTime() : null;
        },
        customerProduct: (i) => i.productionOrder?.customer?.shortName || i.productionOrder?.customer?.name || i.customerName || "",
        product: (i) => i.productName || "",
        checkDate: (i) => new Date(i.checkDate || i.checkTime || i.date || i.createdAt).getTime(),
        qtyLot: (i) => Number(i.qtyInfo?.qtyLot ?? i.lotSize ?? i.qtyLot ?? 0),
        status: (i) => i.status,
      };
      const accessor = accessors[sortField];
      if (accessor) {
        const dir = sortOrder === "asc" ? 1 : -1;
        result = [...result].sort((a, b) => {
          const va = accessor(a);
          const vb = accessor(b);
          if (va == null && vb == null) return 0;
          if (va == null) return 1;
          if (vb == null) return -1;
          if (typeof va === "number" && typeof vb === "number") return ((Number.isNaN(va) ? 0 : va) - (Number.isNaN(vb) ? 0 : vb)) * dir;
          return String(va).localeCompare(String(vb), "vi") * dir;
        });
      }
    }

    return result;
  }, [allItems, statusFilter, dispatchTab, filterValues, headerSearch, sortField, sortOrder]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedItems = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const rowIds = paginatedItems.map((i: { id: string }) => i.id);
  const {
    selectedIds,
    selectionMode,
    toggleSelect,
    handleRowSelect,
    toggleSelectAll,
    handleRowMouseDown,
    handleRowMouseUp,
    handleRowMouseLeave,
    wasLongPress,
    allSelected,
    clearSelection,
    selectedCount,
  } = useRowSelection(rowIds);

  const { handleExport, isExporting } = useExcelExport({
    filename: "danh_sach_kcs_thanh_pham",
    columns: EXPORT_COLUMNS,
    localData: filteredData,
    selectedIds: Array.from(selectedIds),
  });

  const getBulkActions = (): BulkAction[] => {
    if (!selectedCount) return [];
    const actions: BulkAction[] = [];
    actions.push({
      label: "Xuất Excel",
      icon: <Download className="w-3.5 h-3.5" />,
      onClick: () => {
        handleExport();
        clearSelection();
      },
    });
    const selectedItems = allItems.filter((i) => selectedIds.has(i.id));

    if (hasPermission("quality", "approve") || hasPermission("qcFinal", "approve")) {
      const eligibleForPass = selectedItems.filter(
        (i) => i.status === "PENDING" || i.status === "DRAFT"
      );
      if (eligibleForPass.length > 0) {
        actions.push({
          label: `Phê duyệt (Pass) (${eligibleForPass.length})`,
          icon: <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />,
          onClick: async () => {
            await executeBulkAction({
              selectedIds: eligibleForPass.map(i => i.id),
              mutationFn: (id) => trpcClient.qcFinal.updateStatus.mutate({ id: String(id), status: "PASSED" }),
              onSuccess: () => {
                clearSelection();
                queryClient.invalidateQueries({ queryKey: ["qcFinal"] });
              }
            });
          }
        });
      }
    }

    // Lọc các item được chọn hợp lệ cho điều vận giao hàng
    // Đóng gói, nhập kho, chờ xe, tạo phiếu giao là việc của Sản xuất / Giao hàng (/factory/packing), không làm ở phiếu KCS

    if (hasPermission("qcFinal", "delete")) {
      actions.push({
        label: "Xoá",
        icon: <Trash2 className="w-3.5 h-3.5" />,
        onClick: () => {
          executeBulkAction({
            confirmFn: confirm,
            selectedIds: Array.from(selectedIds),
            actionFn: async (id) =>
              (trpcClient as any).qcFinal.delete.mutate({ id }),
            actionName: "xóa",
            itemType: "QC Final",
            onSuccess: () => {
              clearSelection();
              queryClient.invalidateQueries({ queryKey: ["qc-final-all"] });
            },
            requireConfirm: true,
            confirmMessage: `Bạn có chắc muốn xóa ${selectedCount} QC Final đã chọn?`,
          });
        },
        variant: "destructive",
      });
    }
    return actions;
  };

  const handleSave = async (data: any) => {
    try {
      const submitData = {
        ...data,
        aqlStandard: Number(data.aqlStandard) || 2.5,
        checkTime: data.checkTime || new Date(),
      };
      if (editingCheck) {
        await (trpcClient as any).qcFinal.update.mutate({
          id: editingCheck.id,
          data: submitData,
        });
        toast.success("Cập nhật KCS thành công");
      } else {
        await (trpcClient as any).qcFinal.create.mutate(submitData);
        toast.success("Tạo KCS thành công");
      }
      setShowAddSheet(false);
      setEditingCheck(null);
      await queryClient.invalidateQueries({ queryKey: ["qc-final-all"] });
      await queryClient.invalidateQueries({ queryKey: ["qc-final-stats"] });
      await queryClient.invalidateQueries({ queryKey: ["orders"] });
      await queryClient.invalidateQueries({ queryKey: ["production-orders"] });
    } catch (error: any) {
      console.error("QCFinal submit error:", error);
      toast.error(error?.message || "Không thể lưu phiếu KCS");
    }
  };

  const stats = {
    total: statsData?.total ?? allItems.length,
    passedCount:
      statsData?.passedCount ??
      allItems.filter(
        (o: any) =>
          o.status?.toString()?.toUpperCase() === "PASSED" ||
          o.status === "pass",
      ).length,
    withoutDeliveryCount:
      statsData?.withoutDeliveryCount ??
      allItems.filter(
        (o: any) =>
          (o.status === "PASSED" || o.status === "WARNING") &&
          !o.deliveryInfo?.hasDelivery,
      ).length,
    withDeliveryCount:
      statsData?.withDeliveryCount ??
      allItems.filter((o: any) => Boolean(o.deliveryInfo?.hasDelivery)).length,
    warning: statsData?.warningCount ?? 0,
    failed: statsData?.failedCount ?? 0,
    passRate: statsData?.passRate ?? 100,
  };

  const handleQuickCreateNcr = (item: any) => {
    navigate({
      to: "/ncr/create",
      search: {
        sourceType: "FQC",
        sourceId: item.id,
      } as any,
    });
  };

  const filterTags = React.useMemo(() => {
    return STATUS_ITEMS.map((item) => ({
      ...item,
      count:
        allItems?.filter(
          (o: any) =>
            o.status?.toString()?.toUpperCase() === item.key?.toUpperCase(),
        )?.length || 0,
    }));
  }, [allItems]);

  const qcFpiItems = useMemo<CompactKpiItem[]>(() => [
    {
      key: "ALL",
      label: "Tổng Lô Thành Phẩm",
      count: stats.total,
      icon: Box,
      variant: "default",
      tooltip: "Lô nghiệm thu FQC",
    },
    {
      key: "PASSED",
      label: "Đạt Chuẩn FQC",
      count: stats.passedCount,
      icon: CheckCircle2,
      variant: "emerald",
      tooltip: "Đã qua kiểm định thành phẩm",
    },
    {
      key: "WITHOUT_DELIVERY",
      label: "Chưa Tạo Phiếu Giao",
      count: stats.withoutDeliveryCount,
      icon: Truck,
      variant: "amber",
      animate: stats.withoutDeliveryCount > 0,
      tooltip: "Lô đạt chuẩn chưa lên phiếu giao hàng",
    },
    {
      key: "WITH_DELIVERY",
      label: "Đã Tạo Phiếu Giao",
      count: stats.withDeliveryCount,
      icon: CheckCircle2,
      variant: "blue",
      tooltip: "Đã có phiếu giao hàng tương ứng",
    },
  ], [stats]);

  const activeKpiKey =
    dispatchTab !== "ALL" ? dispatchTab : statusFilter || "ALL";

  const handleSelectKpi = (key: string | null) => {
    if (!key || key === "ALL") {
      setStatusFilter(null);
      setDispatchTab("ALL");
    } else if (key === "WITHOUT_DELIVERY") {
      setStatusFilter(null);
      setDispatchTab("WITHOUT_DELIVERY");
    } else if (key === "WITH_DELIVERY") {
      setStatusFilter(null);
      setDispatchTab("WITH_DELIVERY");
    } else if (key === "PASSED") {
      setDispatchTab("ALL");
      setStatusFilter(statusFilter === "PASSED" ? null : "PASSED");
    } else {
      setDispatchTab("ALL");
      setStatusFilter(statusFilter === key ? null : key);
    }
    setCurrentPage(1);
  };

  const qcStatusFilterTags = useMemo(() => [
    { key: "ALL", label: "Tất cả kết quả", count: allItems.length, color: "slate" as const },
    { key: "PASSED", label: "Đạt", count: allItems.filter((o: any) => o.status?.toString()?.toUpperCase() === "PASSED").length, color: "emerald" as const },
    { key: "FAILED", label: "Không đạt", count: allItems.filter((o: any) => o.status?.toString()?.toUpperCase() === "FAILED").length, color: "rose" as const },
    { key: "PENDING", label: "Chờ duyệt", count: allItems.filter((o: any) => o.status?.toString()?.toUpperCase() === "PENDING").length, color: "amber" as const },
  ], [allItems]);

  const dispatchFilterTags = useMemo(() => [
    { key: "ALL", label: "Tất cả xuất giao", count: allItems.length, color: "slate" as const },
    { key: "WITHOUT_DELIVERY", label: "Chưa tạo phiếu giao", count: stats.withoutDeliveryCount, color: "amber" as const },
    { key: "READY_FOR_DISPATCH", label: "Đang chờ gom xe", count: allItems.filter((i) => i.deliveryInfo?.isReadyForDispatch && !i.deliveryInfo?.hasDelivery).length, color: "blue" as const },
    { key: "WITH_DELIVERY", label: "Đã tạo phiếu giao", count: stats.withDeliveryCount, color: "emerald" as const },
  ], [allItems, stats]);

  return (
    <>
      <ListPageShell
        trashModule="qc-final"
        unitLabel="phiếu KCS"
        filterFields={FILTER_FIELDS}
        filterValues={filterValues}
        showFilter={showFilter}
        onToggleFilter={() => setShowFilter((prev) => !prev)}
        onClearAll={handleClearAll}
        totalItems={activeMainTab === "pending_fqc" ? (tabCounts?.pendingFqc || 0) : filteredData.length}
        currentPage={activeMainTab === "pending_fqc" ? 1 : currentPage}
        pageSize={activeMainTab === "pending_fqc" ? Math.max(100, (tabCounts?.pendingFqc || 100)) : itemsPerPage}
        onPageChange={setCurrentPage}
        onPageSizeChange={() => {}}
        tabs={
          <div className="flex items-center gap-4">
            <button
              type="button"
              data-testid="tab-qc-reports"
              onClick={() => handleMainTabChange("qc_reports")}
              className={emsTabClass(activeMainTab === "qc_reports", { size: "sm" })}
            >
              <FileText className="w-3.5 h-3.5 text-indigo-600" />
              <span>Lịch Sử FQC</span>
              <span
                className={cn(
                  "px-1.5 py-0.2 rounded-full text-[10px] font-bold tabular-nums",
                  activeMainTab === "qc_reports"
                    ? "bg-primary/10 text-primary"
                    : "bg-slate-200 text-slate-600"
                )}
              >
                {tabCounts?.all ?? allItems.length}
              </span>
            </button>

            <button
              type="button"
              data-testid="tab-pending-fqc"
              onClick={() => handleMainTabChange("pending_fqc")}
              className={emsTabClass(activeMainTab === "pending_fqc", { size: "sm" })}
            >
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
              <span>Chờ Kiểm FQC</span>
              <span
                className={cn(
                  "px-1.5 py-0.2 rounded-full text-[10px] font-bold tabular-nums",
                  activeMainTab === "pending_fqc"
                    ? "bg-primary/10 text-primary"
                    : "bg-slate-200 text-slate-600"
                )}
              >
                {tabCounts?.pendingFqc ?? 0}
              </span>
            </button>
          </div>
        }
        headerFilters={
          activeMainTab === "qc_reports" ? (
            <div className="flex items-center gap-2 flex-wrap">
              <PageFilterTags
                items={qcStatusFilterTags}
                selectedKeys={!statusFilter || statusFilter === "ALL" ? [] : [statusFilter]}
                onSelect={(key) => {
                  setStatusFilter(!key || key === "ALL" ? null : key);
                  setCurrentPage(1);
                }}
                dropdownLabel="Kết quả KCS"
              />
              <div className="w-[1px] h-5 bg-slate-200 mx-0.5 hidden sm:block" />
              <PageFilterTags
                items={dispatchFilterTags}
                selectedKeys={dispatchTab === "ALL" ? [] : [dispatchTab]}
                onSelect={(key) => {
                  setDispatchTab((key || "ALL") as any);
                  setCurrentPage(1);
                }}
                dropdownLabel="Tiến độ xuất giao"
              />
              {/* Nút gom nhóm theo ngày là tuỳ chọn hiển thị, không phải bộ lọc thời gian → đặt cuối hàng lọc */}
              <div className="w-[1px] h-5 bg-slate-200 mx-0.5 hidden sm:block" />
              <DateGroupToggleButton
                isGrouped={isDateGrouped}
                onToggle={() => setIsDateGrouped(!isDateGrouped)}
              />
            </div>
          ) : undefined
        }
        actions={
          <div className="flex gap-2 items-center">
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs bg-white border-blue-200 text-blue-700 hover:bg-blue-50 shadow-xs"
              onClick={() => setShowReportModal(true)}
            >
              <Printer className="mr-1.5 h-3.5 w-3.5 text-blue-600" /> Báo Cáo KCS
            </Button>
            <ExportImportButtons
              onExport={() => handleExport()}
              isExporting={isExporting}
              onDownloadTemplate={() => downloadExcelTemplate(EXPORT_COLUMNS, "mau_kcs_thanh_pham")}
            >
              {hasPermission("qcFinal", "create") && (
                <Button
                  size="sm"
                  className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700"
                  onClick={() => {
                    setEditingCheck(null);
                    setShowAddSheet(true);
                  }}
                >
                  <Plus className="mr-2 h-3 w-3" /> KCS Thành Phẩm
                </Button>
              )}
            </ExportImportButtons>
          </div>
        }
      >
        {activeMainTab === "pending_fqc" ? (
          <PendingFqcQueueTab />
        ) : (
          <Card className="flex-1 p-0 border-slate-200 shadow-sm overflow-auto bg-white">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
              <span className="ml-2 text-slate-500">Đang tải dữ liệu...</span>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full text-red-500">
              Lỗi khi tải dữ liệu. Vui lòng thử lại.
            </div>
          ) : (
            <Table className={cn("min-w-[1250px]", selectionMode && "apv-select-mode")}>
                <TableHeader className="apv-table-header">
                  <TableRow className="border-b-0 hover:bg-transparent">
                    <TableHead
                      className={cn("apv-col-checkbox w-10 text-center px-4 whitespace-nowrap")}
                    >
                      <DataTableSelectAllCheckbox
                        rowSelection={
                          { allSelected, toggleSelectAll, selectionMode } as any
                        }
                        asChild
                      />
                    </TableHead>
                    <SortableHeader label="Mã phiếu & tình trạng" field="finalQcId" currentField={sortField} currentOrder={sortOrder} onSort={handleSort} className="apv-col-code font-semibold w-[170px]" />
                    <SortableHeader label="Hạn giao & OTIF" field="deadline" currentField={sortField} currentOrder={sortOrder} onSort={handleSort} className="font-semibold w-[130px]" />
                    <SortableHeader label="Khách hàng" field="customerProduct" currentField={sortField} currentOrder={sortOrder} onSort={handleSort} className="font-semibold w-[120px] min-w-[110px]" />
                    <SortableHeader label="Sản phẩm" field="product" currentField={sortField} currentOrder={sortOrder} onSort={handleSort} className="font-semibold w-[260px] min-w-[220px]" />
                    <SortableHeader label="Ngày kiểm" field="checkDate" currentField={sortField} currentOrder={sortOrder} onSort={handleSort} className="font-semibold w-[100px]" />
                    <SortableHeader label="Số lượng" field="qtyLot" currentField={sortField} currentOrder={sortOrder} onSort={handleSort} className="text-right font-semibold w-[210px]" />
                    <TableHead className="text-center font-semibold w-[160px] whitespace-nowrap">
                      Phương pháp &amp; Lỗi
                    </TableHead>
                    <TableHead className="text-center font-semibold w-[200px] whitespace-nowrap">
                      Phiếu đóng gói
                    </TableHead>
                    <TableHead className="apv-col-actions w-[50px] text-right whitespace-nowrap"></TableHead>
                  </TableRow>
                  {showFilter && (
                    <InlineFilterRow
                      columns={[
                        CHECKBOX_COL_NULL,
                        { key: "finalQcId" },
                        null,
                        { key: "customerProduct" },
                        null,
                        null,
                        null,
                        { key: "status" },
                        { key: "dispatchStatus" },
                        null,
                      ]}
                      fields={FILTER_FIELDS}
                      values={filterValues}
                      onChange={handleFilterChange}
                    />
                  )}
                </TableHeader>
                <TableBody>
                  <DateGroupedTable<any>
                    items={paginatedItems}
                    isGrouped={isDateGrouped && !sortField}
                    getDateKey={(item) =>
                      extractDateKey(
                        item.checkDate ||
                          (item as any).checkTime ||
                          (item as any).check_time ||
                          item.date ||
                          (item as any).createdAt,
                      )
                    }
                    emptyState={
                      <TableRow>
                        <TableCell
                          colSpan={10}
                          className="h-32 text-center text-slate-400"
                        >
                          {statusFilter
                            ? "Không có phiếu nào ở trạng thái này"
                            : "Chưa có phiếu QC nào"}
                        </TableCell>
                      </TableRow>
                    }
                    renderGroupHeader={(group) => {
                      const groupLotSize = group.items.reduce(
                        (s, it) => s + (Number(it.qtyInfo?.qtyLot) || Number(it.lotSize) || Number((it as any).qtyLot) || 0),
                        0,
                      );
                      const groupRejected = group.items.reduce((s, it) => s + (Number(it.qtyFailed) || 0), 0);
                      const groupCheckQty = group.items.reduce(
                        (s, it) => s + (Number(it.checkQty) || Number((it as any).qtyChecked) || 0),
                        0,
                      );
                      const groupErrorQty = group.items.reduce(
                        (s, it) => s + (Number(it.errorQty) || Number((it as any).qtyFailed) || 0),
                        0,
                      );
                      const groupPassQty = Math.max(0, groupCheckQty - groupErrorQty);
                      const groupPassRate =
                        groupCheckQty > 0 ? Math.round((groupPassQty / groupCheckQty) * 100) : 100;

                      return (
                        <TableRow
                          key={`group-${group.dateKey}`}
                          className={cn(
                            "border-y font-semibold text-xs transition-colors select-none",
                            group.isToday
                              ? "bg-[#eef2ff] hover:bg-[#e0e7ff] border-indigo-200"
                              : "bg-[#f1f5f9] hover:bg-[#e2e8f0] border-slate-300",
                          )}
                        >
                          {/* Sticky Left: Checkbox + Mã phiếu */}
                          <TableCell
                            colSpan={2}
                            className="py-2.5 px-4 font-bold text-slate-900 border-r border-slate-200"
                            style={{
                              backgroundColor: group.isToday ? "#eef2ff" : "#f1f5f9",
                              isolation: "isolate",
                            }}
                          >
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 p-0 text-slate-600 hover:text-slate-900 shrink-0"
                                onClick={group.toggleCollapse}
                                title={group.isCollapsed ? "Bung ra" : "Thu gọn"}
                              >
                                {group.isCollapsed ? (
                                  <ChevronRight size={16} />
                                ) : (
                                  <ChevronDown size={16} />
                                )}
                              </Button>
                              <Calendar className="w-4 h-4 text-indigo-600 shrink-0" />
                              <span className="text-[13px] font-bold text-slate-900">
                                {group.formattedDate}
                              </span>
                            </div>
                          </TableCell>

                          {/* Columns 3-6: Info + Badges */}
                          <TableCell colSpan={4} className="py-2 px-3 text-slate-700">
                            <div className="flex items-center gap-2">
                              <Badge
                                variant="outline"
                                className="bg-white/90 text-slate-800 border-slate-300 text-[11px] font-bold px-2 py-0.5"
                              >
                                {group.count} phiếu KCS
                              </Badge>
                              {group.isToday && (
                                <Badge className="bg-indigo-600 text-white text-[10px] font-bold px-2 py-0 border-0">
                                  HÔM NAY
                                </Badge>
                              )}
                              {group.isCollapsed && (
                                <span className="text-[11px] text-slate-500 font-normal italic">
                                  (Đang thu gọn - bấm để xem chi tiết)
                                </span>
                              )}
                            </div>
                          </TableCell>

                          {/* Column 7: Số lượng */}
                          <TableCell className="text-right py-2 px-3 font-mono font-bold text-slate-900 text-xs">
                            <div className="flex items-center justify-end gap-2 whitespace-nowrap">
                              <span className="text-slate-900">Lô: {formatNumber(groupLotSize)}</span>
                              <span className="text-[11px] text-rose-600">· loại {formatNumber(groupRejected)}</span>
                            </div>
                          </TableCell>

                          {/* Column 8: Phương pháp & Lỗi */}
                          <TableCell className="text-center py-2 px-3 font-mono text-xs">
                            <div className="flex items-center justify-center gap-2 whitespace-nowrap" title={`Đạt ${formatNumber(groupPassQty)} / ${formatNumber(groupCheckQty)}`}>
                                <span className="font-bold text-emerald-700">Đạt {groupPassRate}%</span>
                                {groupErrorQty > 0 && (
                                  <span className="font-bold text-rose-600">
                                    ({formatNumber(groupErrorQty)} lỗi)
                                  </span>
                                )}
                              </div>
                          </TableCell>

                          {/* Columns 9-10: Empty */}
                          <TableCell colSpan={2} className="py-2 px-3 text-slate-400 text-center text-xs">
                            —
                          </TableCell>
                        </TableRow>
                      );
                    }}
                    renderRow={(item) => (
                      <DataTableRow
                        key={item.id}
                        id={item.id}
                        rowSelection={
                          {
                            selectedIds,
                            handleRowMouseDown,
                            handleRowMouseUp,
                            handleRowMouseLeave,
                            wasLongPress,
                            selectionMode,
                            handleRowSelect,
                          } as any
                        }
                        onClick={(e) => {
                          if (selectionMode) return;
                          navigate({
                            to: "/qc-final/$checkId",
                            params: { checkId: item.id },
                          });
                        }}
                      >
                        <DataTableRowCheckbox
                          id={item.id}
                          rowSelection={{ selectedIds, toggleSelect } as any}
                        />
                        <TableCell className="apv-col-code py-1.5 align-middle">
                          <div className="flex flex-col gap-1.5 items-start">
                            <span
                              className="font-bold text-indigo-600 text-[13px] leading-none group-hover:underline cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate({
                                  to: "/qc-final/$checkId",
                                  params: { checkId: item.id },
                                });
                              }}
                            >
                              {item.finalQcId || item.id}
                            </span>
                            <StatusBadge type="QCStatus" value={item.status} />
                            <FqcStageBadge item={item} />
                          </div>
                        </TableCell>
                        <TableCell className="align-middle">
                          <div className="flex flex-col gap-1">
                            {item.otifInfo?.deadline ? (
                              <div className="flex items-center gap-1.5 text-xs font-mono font-semibold text-slate-800">
                                <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                <span>
                                  {new Date(item.otifInfo.deadline).toLocaleDateString("vi-VN")}
                                </span>
                              </div>
                            ) : (
                              <span className="text-slate-400 italic text-xs">Chưa đặt hạn</span>
                            )}
                            <div className="flex items-center gap-1 flex-wrap">
                              {item.otifInfo?.isUrgent && (
                                <Badge className="bg-rose-600 text-white text-[9px] px-1 py-0 h-4 font-bold">
                                  🔥 GẤP
                                </Badge>
                              )}
                              {item.otifInfo?.status === "OVERDUE" && (
                                <Badge variant="destructive" className="text-[9px] px-1 py-0 h-4 font-bold">
                                  Quá hạn {Math.abs(item.otifInfo.countdownHours || 0)}h
                                </Badge>
                              )}
                              {item.otifInfo?.status === "DUE_SOON" && (
                                <Badge className="bg-amber-500 text-white text-[9px] px-1 py-0 h-4 font-bold">
                                  Còn {item.otifInfo.countdownHours}h
                                </Badge>
                              )}
                              {item.otifInfo?.status === "ON_TIME" && (
                                <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50 text-[9px] px-1 py-0 h-4 font-medium">
                                  Còn {item.otifInfo.countdownHours}h
                                </Badge>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="align-middle">
                          <span
                            data-testid="fqc-customer"
                            style={{ display: "-webkit-box", maxWidth: 120 }}
                            className="font-semibold text-indigo-900 text-[12px] leading-snug line-clamp-2 whitespace-normal break-words"
                            title={item.productionOrder?.customer?.name || item.customerName}
                          >
                            {item.productionOrder?.customer?.shortName ||
                              item.productionOrder?.customer?.name ||
                              item.customer?.shortName ||
                              item.customerName ||
                              "—"}
                          </span>
                        </TableCell>
                        <TableCell className="align-middle">
                          <div className="flex items-center gap-2.5 w-[260px] max-w-[260px]">
                            <ProductionThumbnailCell
                              imageUrl={
                                item.product?.image ||
                                item.productionOrder?.product?.image ||
                                item.productionOrder?.orderDetail?.product?.image ||
                                item.productImage
                              }
                              layoutUrl={
                                item.printLayout?.fileUrl ||
                                item.productionOrder?.printLayouts?.[0]?.fileUrl ||
                                item.productionOrder?.orderDetail?.printLayout?.fileUrl ||
                                item.layoutUrl
                              }
                              title={item.productName || item.finalQcId}
                              subtitle={item.finalQcId}
                              type="auto"
                              size={32}
                              className="shrink-0"
                            />
                            <div className="flex flex-col gap-1 min-w-0 flex-1">
                              <span
                                data-testid="fqc-product"
                                className="font-semibold text-slate-900 text-[13px] line-clamp-2 leading-snug whitespace-normal break-words"
                                title={item.productName}
                              >
                                {item.productName}
                              </span>
                              <span className="text-[11px] text-slate-500 font-mono leading-none">
                                {item.productionOrder?.orderId || item.productionOrderId}
                              </span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="align-middle">
                          <span className="text-[12px] text-slate-600">
                            {(() => {
                              const d = new Date(
                                (item as any).checkTime ||
                                  (item as any).check_time ||
                                  item.date ||
                                  (item as any).createdAt,
                              );
                              return isNaN(d.getTime())
                                ? "—"
                                : d.toLocaleDateString("vi-VN");
                            })()}
                          </span>
                        </TableCell>
                        <TableCell className="text-right align-middle">
                          {(() => {
                            const q = item.qtyInfo || {};
                            const row = (label: string, value: React.ReactNode, cls = "text-slate-800") => (
                              <div className="flex items-baseline justify-end gap-1 leading-tight whitespace-nowrap">
                                <span className="text-[10px] text-slate-500">{label}</span>
                                <span className={cn("font-mono text-[12px] font-semibold", cls)}>{value}</span>
                              </div>
                            );
                            return (
                              <div data-testid="fqc-qty" className="grid grid-cols-2 gap-x-3 gap-y-0.5 min-w-[200px]">
                                {row("SL lô", formatNumber(q.qtyLot ?? item.qtyPassed ?? 0), "text-slate-900 font-bold")}
                                {row("SL loại", formatNumber(item.qtyFailed || 0), item.qtyFailed ? "text-rose-600" : "text-slate-500")}
                                {row("SL đặt", q.qtyOrdered ? formatNumber(q.qtyOrdered) : "—")}
                                {row("Lũy kế", formatNumber(q.qtyCumulative || 0), "text-indigo-700")}
                                {q.qtyOrdered
                                  ? q.qtyShortage > 0
                                    ? row("Còn thiếu", formatNumber(q.qtyShortage), "text-rose-600")
                                    : row("Còn thiếu", "Đủ", "text-emerald-600")
                                  : null}
                              </div>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex flex-col items-center gap-1">
                            <Badge
                              variant="outline"
                              className="text-[10px] px-1.5 py-0 h-4 border-slate-200 bg-slate-50 leading-tight"
                            >
                              {(item as any).aqlLevel ? `AQL ${(item as any).aqlLevel}` : item.method || "AQL II"}
                            </Badge>
                            <div className="flex items-center gap-2">
                              <span className="text-[11px] font-bold text-rose-600 leading-none">
                                Err: {(item as any).qtyFailed || item.errorQty || 0}
                              </span>
                              {(item as any).acValue !== undefined && (
                                <span className="text-[10px] text-slate-500 leading-none">
                                  (Ac ≤ {(item as any).acValue})
                                </span>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1 justify-center flex-wrap">
                            {/* Phiếu đóng gói của lô (Sản xuất) — KCS chỉ xem, thao tác ở màn Đóng gói */}
                            {item.packingSlip ? (
                              <button
                                type="button"
                                data-testid="fqc-packing-slip-link"
                                className="flex flex-col items-center gap-0.5 text-[11px] leading-tight cursor-pointer hover:underline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate({ to: "/factory/packing", search: { q: item.packingSlip.code } as any });
                                }}
                                title="Mở phiếu đóng gói ở Xưởng sản xuất › Đóng gói & Nhập kho"
                              >
                                <span className="font-mono font-bold text-indigo-700">{item.packingSlip.code}</span>
                                <span className="text-slate-500">
                                  {item.packingSlip.status === "PACKED"
                                    ? `${item.packingSlip.packer?.fullName || "—"} · đạt ${formatNumber(item.packingSlip.qtyPacked ?? 0)}${item.packingSlip.qtyDefect ? ` · lỗi ${formatNumber(item.packingSlip.qtyDefect)}` : ""}`
                                    : "Chờ Sản xuất đóng gói"}
                                </span>
                              </button>
                            ) : (
                              <span className="text-slate-400 text-xs">—</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell
                          className="apv-col-actions px-4 text-right"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                className="h-7 w-7 p-0 hover:bg-slate-200"
                              >
                                <MoreHorizontal className="h-4 w-4 text-slate-400" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              className="w-[190px]"
                            >
                              <DropdownMenuLabel>Thao tác</DropdownMenuLabel>
                              {hasPermission("qcFinal", "update") && (
                                <DropdownMenuItem
                                  onClick={() => {
                                    setEditingCheck(item);
                                    setShowAddSheet(true);
                                  }}
                                >
                                  <Edit className="w-4 h-4 mr-2 text-slate-500" />{" "}
                                  Chỉnh sửa
                                </DropdownMenuItem>
                              )}
                              {(item.status === "FAILED" || item.status === "fail") && (
                                <DropdownMenuItem
                                  className="text-rose-600 focus:text-rose-700"
                                  onClick={() => handleQuickCreateNcr(item)}
                                >
                                  <AlertOctagon className="w-4 h-4 mr-2" /> Tạo NCR Sự Cố
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </DataTableRow>
                    )}
                  />

                  {/* Page Subtotal Footer Row */}
                  {paginatedItems.length > 0 && (() => {
                    const pageLotSize = paginatedItems.reduce(
                      (s, it) => s + (Number(it.qtyInfo?.qtyLot) || Number(it.lotSize) || Number((it as any).qtyLot) || 0),
                      0,
                    );
                    const pageRejected = paginatedItems.reduce((s, it) => s + (Number(it.qtyFailed) || 0), 0);
                    const pageCheckQty = paginatedItems.reduce(
                      (s, it) => s + (Number(it.checkQty) || Number((it as any).qtyChecked) || 0),
                      0,
                    );
                    const pageErrorQty = paginatedItems.reduce(
                      (s, it) => s + (Number(it.errorQty) || Number((it as any).qtyFailed) || 0),
                      0,
                    );
                    const pagePassQty = Math.max(0, pageCheckQty - pageErrorQty);
                    const pagePassRate =
                      pageCheckQty > 0 ? Math.round((pagePassQty / pageCheckQty) * 100) : 100;

                    return (
                      <TableRow
                        className="border-t-2 border-slate-300 font-bold text-xs"
                        style={{ backgroundColor: "#f8fafc", isolation: "isolate" }}
                      >
                        <TableCell colSpan={2} className="py-3 px-4 text-slate-900 border-r border-slate-200">
                          <div className="flex items-center gap-2">
                            <span className="font-bold uppercase tracking-wider text-[11px] text-slate-600">
                              Tổng Trang:
                            </span>
                            <Badge variant="outline" className="bg-white text-slate-900 border-slate-300 font-bold px-2 py-0.5">
                              {paginatedItems.length} phiếu KCS
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell colSpan={4} className="py-3 px-3 text-slate-500 font-medium text-xs">
                          Tổng số liệu nghiệm thu KCS trên trang hiện tại
                        </TableCell>
                        <TableCell className="text-right py-3 px-3 font-mono font-bold text-slate-900 text-xs">
                          <div className="flex items-center justify-end gap-2 whitespace-nowrap">
                            <span className="text-slate-900">Lô: {formatNumber(pageLotSize)}</span>
                            <span className="text-[11px] text-rose-600">· loại {formatNumber(pageRejected)}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center py-3 px-3 font-mono text-xs">
                          <div className="flex items-center justify-center gap-2 whitespace-nowrap" title={`Đạt ${formatNumber(pagePassQty)} / ${formatNumber(pageCheckQty)}`}>
                              <span className="font-bold text-emerald-700">Đạt {pagePassRate}%</span>
                              {pageErrorQty > 0 && (
                                <span className="font-bold text-rose-600">
                                  ({formatNumber(pageErrorQty)} lỗi)
                                </span>
                              )}
                            </div>
                        </TableCell>
                        <TableCell colSpan={2} className="py-3 px-3 text-slate-400 text-center text-xs">
                          —
                        </TableCell>
                      </TableRow>
                    );
                  })()}
                </TableBody>
              </Table>
          )}
        </Card>
      )}
      </ListPageShell>

      <QCFinalForm
        open={showAddSheet}
        onOpenChange={(open) => {
          setShowAddSheet(open);
          if (!open) setEditingCheck(null);
        }}
        onSubmit={handleSave}
        initialData={editingCheck}
      />
      <FinishedGoodsQCReportPrintModal
        open={showReportModal}
        onClose={() => setShowReportModal(false)}
      />
      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={getBulkActions()}
      />

    </>
  );
}
